/**
 * Node data for every workflow entry point — the news monitor, the platform
 * listener, and the webhook/manual/schedule/agent-input/plugin triggers.
 */

import type { PlatformListenerIdleConfig } from "./platform-listener-idle.schema";
import type { DataSchemaType } from "./schema-type";
import { MonitorProvider } from "./trigger-source";
import type { BaseNodeData } from "./workflow-node-base.types";

export type NewsMonitorTriggerNodeData = BaseNodeData & {
  type: "newsMonitorTrigger";
  monitorProvider: MonitorProvider | "";
  currentTab?: "setup" | "configure" | "test";
  query: string;
  frequency: string;
  monitorId: string;
  webhookId: string;
  webhookUrl: string;
  exampleData: Record<string, unknown>;
  metadata: Record<string, string>;
  /** User-defined output fields — shapes the structured data returned per event */
  outputSchema?: Record<string, DataSchemaType>;
  /** Max results per execution (provider-specific, e.g. Exa) */
  count?: number;
  /** Time of day for the monitor to run — only for providers with requiresTimeOfDay (e.g. "09:00") */
  timeOfDay?: string;
  /** IANA timezone for the monitor schedule (e.g. "Europe/Brussels") */
  timezone?: string;
};

/** Display metadata for news monitor providers — drives the frontend selection UI.
 *  `icon` maps to a BrandIconName on the frontend (e.g. "parallel-ai", "exaai"). */
export const MONITOR_PROVIDERS = [
  {
    monitorProvider: MonitorProvider.PARALLEL_AI,
    label: "Parallel AI",
    description: "AI-powered news monitoring with NLP search",
    icon: "parallel-ai",
    supportsCount: false,
    frequencies: ["1h", "3h", "6h", "12h", "1d", "7d", "14d", "30d"],
    requiresTimeOfDay: false,
    disabled: false
  },
  {
    monitorProvider: MonitorProvider.EXA,
    label: "Exa",
    description: "Web-scale monitoring with structured data enrichment — coming soon",
    icon: "exaai",
    supportsCount: true,
    frequencies: ["1d", "7d", "30d"],
    requiresTimeOfDay: true,
    disabled: true
  }
] as const satisfies ReadonlyArray<{
  monitorProvider: MonitorProvider;
  label: string;
  description: string;
  icon: string;
  supportsCount: boolean;
  frequencies: readonly string[];
  requiresTimeOfDay: boolean;
  disabled: boolean;
}>;

export type PlatformListenerFilterCondition = {
  field: string;
  operator: "eq" | "neq" | "in" | "nin" | "gt" | "gte" | "lt" | "lte";
  value: string | string[] | number;
};

export type PlatformListenerFilterGroup = {
  logic: "AND" | "OR";
  conditions: PlatformListenerFilterCondition[];
};

export type PlatformListenerFilterConfig = {
  logic: "AND" | "OR";
  conditions: PlatformListenerFilterCondition[];
  groups: PlatformListenerFilterGroup[];
};

export type PlatformListenerTriggerNodeData = BaseNodeData & {
  type: "platformListenerTrigger";
  platformEventType: string;
  filters: PlatformListenerFilterConfig;
  /**
   * Idle-event-specific configuration block. Present iff `platformEventType`
   * is `"conversation.idle"` — the trigger's deploy adapter rejects a
   * conversation.idle listener without this block. Validated at the boundary
   * with `PlatformListenerIdleConfigSchema`.
   */
  idleConfig?: PlatformListenerIdleConfig;
  exampleData: Record<string, unknown>;
  currentTab?: "setup" | "configure" | "test";
};

export type WebhookTriggerNodeData = BaseNodeData & {
  exampleData: Record<string, unknown>;
  type: "webhookTrigger"; // TODO: Delete
  webhookId: string;
  webhookUrl: string;
};

export type ManualTriggerNodeData = BaseNodeData & {
  type: "manualTrigger";
  exampleData: Record<string, unknown>;
};

export type ScheduleTriggerNodeData = BaseNodeData & {
  endDate?: string;
  interval: string;
  intervalValue: number;
  startDate?: string;
  timezone?: string;
  type: "scheduleTrigger"; // TODO: Delete
};

export type AgentInputTriggerNodeData = BaseNodeData & {
  parameters: Record<string, DataSchemaType>;
  required?: string[];
  jsonSchemaError?: string | null;
  jsonValuesError?: string | null;
  includeMetadata?: boolean;
  type: "agentInputTrigger"; // TODO: Delete
};

export type PluginTriggerNodeData = BaseNodeData & {
  type: "pluginTrigger";
  componentKey: string;
  config: Record<string, unknown>;
  currentTab: "setup" | "configure" | "test";
  exampleData: Record<string, unknown>;
  pipedreamTriggerId: string;
  selectedAccount: Record<string, unknown>;
  selectedToolId: string;
  timerIntervalSeconds?: number;
  triggerConfig: { data: unknown } | null; // Compatible with DeployTriggerResponse from @pipedream/sdk
  webhookId: string;
  webhookUrl: string;
};

export type SelectTriggerNodeData = {};
